import { supabase } from '@/integrations/supabase/client';
import type {
  Material,
  Recipe,
  InventoryTransaction,
  PhysicalInventoryCount,
  Expense,
  Order,
  InventorySnapshot,
  AnalyticsDateRange,
  MaterialCostHistory,
  WasteMetrics,
  WasteMetricsDb,
  FinancialMetrics,
  InventoryMetrics,
  MaterialWithStatus,
  StockAlert,
  MaterialUsageMetric,
  RecipeCostAnalysis
} from '../types';
import { CostCalculator } from '../calculators/CostCalculator';
import { WasteCalculator } from '../calculators/WasteCalculator';
import { FinancialCalculator } from '../calculators/FinancialCalculator';

/**
 * Input Validation Helper
 */
class InputValidator {
  static validateQuantity(quantity: number, fieldName: string = 'Quantity'): void {
    if (typeof quantity !== 'number' || isNaN(quantity)) {
      throw new Error(`${fieldName} must be a valid number`);
    }
    if (quantity < 0) {
      throw new Error(`${fieldName} cannot be negative`);
    }
  }

  static validatePrice(price: number, fieldName: string = 'Price'): void {
    if (typeof price !== 'number' || isNaN(price)) {
      throw new Error(`${fieldName} must be a valid number`);
    }
    if (price < 0) {
      throw new Error(`${fieldName} cannot be negative`);
    }
  }

  static validateDateRange(dateRange: AnalyticsDateRange): void {
    const start = new Date(dateRange.startDate);
    const end = new Date(dateRange.endDate);
    
    if (isNaN(start.getTime())) {
      throw new Error('Start date is invalid');
    }
    if (isNaN(end.getTime())) {
      throw new Error('End date is invalid');
    }
    if (start > end) {
      throw new Error('Start date cannot be after end date');
    }
  }

  static validateBranchId(branchId: string): void {
    if (!branchId || typeof branchId !== 'string' || branchId.trim().length === 0) {
      throw new Error('Branch ID is required');
    }
  }
}

/**
 * AnalyticsService - Single Source of Truth for Inventory Analytics
 * 
 * This service integrates all calculators and provides unified analytics data.
 * It replaces multiple duplicate implementations in:
 * - InventoryAnalytics.tsx
 * - CostAnalysis.tsx
 * - AdvancedCostAnalytics.tsx
 * - BusinessInsightsDashboard.tsx
 * - AdvancedAnalyticsDashboard.tsx
 */
export class AnalyticsService {
  private costCalculator: CostCalculator;
  private wasteCalculator: WasteCalculator;
  private financialCalculator: FinancialCalculator;

  constructor() {
    this.costCalculator = new CostCalculator();
    this.wasteCalculator = new WasteCalculator();
    this.financialCalculator = new FinancialCalculator();
  }

  /**
   * Get comprehensive financial metrics for a date range
   */
  async getFinancialMetrics(
    branchId: string,
    dateRange: AnalyticsDateRange
  ): Promise<FinancialMetrics> {
    // Validate inputs
    InputValidator.validateBranchId(branchId);
    InputValidator.validateDateRange(dateRange);

    try {
      // Fetch all required data in parallel
      const [orders, expenses, transactions, materials, recipes] = await Promise.all([
        this.getOrders(branchId, dateRange),
        this.getExpenses(branchId, dateRange),
        this.getTransactions(branchId, dateRange),
        this.getMaterials(branchId),
        this.getRecipesWithIngredients(branchId)
      ]);

      // Calculate revenue from actual orders
      const revenue = this.financialCalculator.calculateRevenue(orders);

      // Calculate COGS from actual orders (not inventory deductions!)
      // Type assertion since getRecipesWithIngredients returns recipes with ingredients
      const cogs = this.costCalculator.calculateCOGS(orders, recipes as any, materials);

      // Calculate gross profit
      const grossProfit = revenue - cogs;
      const grossProfitMargin = this.financialCalculator.calculateGrossProfitMargin(revenue, cogs);

      // Calculate total expenses
      const totalExpenses = expenses.reduce((sum, exp) => sum + exp.amount, 0);

      // Calculate net profit
      const netProfit = this.financialCalculator.calculateNetProfit(revenue, cogs, expenses);

      // Calculate net profit margin
      const netProfitMargin = revenue > 0 ? (netProfit / revenue) * 100 : 0;

      // Calculate average inventory value
      const avgInventoryValue = this.calculateAverageInventoryValue(materials, transactions);

      // Calculate stock turnover rate (CORRECT formula)
      const stockTurnoverRate = this.costCalculator.calculateStockTurnoverRate(
        cogs,
        avgInventoryValue,
        avgInventoryValue // Using same value as approximation for now
      );

      // Calculate COGS ratio
      const cogsRatio = this.financialCalculator.calculateCOGSRatio(revenue, cogs);

      return {
        revenue,
        cogs,
        grossProfit,
        grossProfitMargin,
        totalExpenses,
        netProfit,
        netProfitMargin,
        averageInventoryValue: avgInventoryValue,
        stockTurnoverRate,
        cogsRatio,
        period: dateRange
      };
    } catch (error) {
      console.error('[AnalyticsService] Error in getFinancialMetrics:', error);
      // Return zero metrics instead of throwing
      return {
        revenue: 0,
        cogs: 0,
        grossProfit: 0,
        grossProfitMargin: 0,
        totalExpenses: 0,
        netProfit: 0,
        netProfitMargin: 0,
        averageInventoryValue: 0,
        stockTurnoverRate: 0,
        cogsRatio: 0,
        period: dateRange
      };
    }
  }

  /**
   * Get comprehensive waste metrics
   */
  async getWasteMetrics(
    branchId: string,
    dateRange: AnalyticsDateRange
  ): Promise<WasteMetrics> {
    try {
      const [materials, transactions, physicalCounts, recipes] = await Promise.all([
        this.getMaterials(branchId),
        this.getTransactions(branchId, dateRange),
        this.getPhysicalCounts(branchId, dateRange),
        this.getRecipes(branchId)
      ]);

      // Use calculator to get waste metrics properly
      // Type assertion - our InventoryTransaction matches what the calculator needs
      const calculatorResult: WasteMetricsDb = this.wasteCalculator.calculateTotalWaste(
        materials,
        transactions as any,
        physicalCounts as any,
        undefined, // snapshots
        { start_date: dateRange.startDate, end_date: dateRange.endDate }
      ) as unknown as WasteMetricsDb;

      // Map to WasteMetrics interface (snake_case to camelCase)
      const wasteMetrics: WasteMetrics = {
        totalWaste: calculatorResult.total_waste_quantity,
        totalWasteCost: calculatorResult.total_waste_cost,
        wastePercentage: calculatorResult.waste_percentage,
        significantWaste: (calculatorResult.waste_by_material || []).map(item => ({
          material_id: item.material_id,
          material_name: item.material_name,
          waste_amount: item.waste_quantity,
          waste_cost: item.waste_cost,
          waste_percentage: item.waste_percentage
        })),
        recommendations: [],
        period: dateRange
      };

      // Generate recommendations based on the waste data
      wasteMetrics.recommendations = this.generateWasteRecommendations(calculatorResult);

      return wasteMetrics;
    } catch (error) {
      console.error('[AnalyticsService] Error in getWasteMetrics:', error);
      return {
        totalWaste: 0,
        totalWasteCost: 0,
        wastePercentage: 0,
        significantWaste: [],
        recommendations: ['Unable to calculate waste metrics'],
        period: dateRange
      };
    }
  }

  /**
   * Get comprehensive inventory metrics
   */
  async getInventoryMetrics(
    branchId: string,
    dateRange: AnalyticsDateRange
  ): Promise<InventoryMetrics> {
    try {
      const [materials, transactions] = await Promise.all([
        this.getMaterials(branchId),
        this.getTransactions(branchId, dateRange)
      ]);

      // Calculate current inventory value
      const totalInventoryValue = materials.reduce(
        (sum, m) => sum + (m.current_stock * m.cost_per_unit),
        0
      );

      // Count stock alerts
      const lowStockItems = materials.filter(
        (m) => m.reorder_point && m.current_stock <= m.reorder_point
      );
      const outOfStockItems = materials.filter((m) => m.current_stock <= 0);
      const overstockItems = materials.filter(
        (m) => m.max_stock && m.current_stock > m.max_stock
      );

      // Calculate total materials (filter out inactive on client side if is_active exists)
      const totalMaterials = materials.filter((m) => m.is_active !== false).length;

      // Calculate average stock level
      const avgStockLevel = materials.length > 0
        ? materials.reduce((sum, m) => sum + m.current_stock, 0) / materials.length
        : 0;

      // Get stock alerts
      const stockAlerts = this.generateStockAlerts(materials);

      return {
        totalInventoryValue,
        totalMaterials,
        lowStockCount: lowStockItems.length,
        outOfStockCount: outOfStockItems.length,
        overstockCount: overstockItems.length,
        averageStockLevel: avgStockLevel,
        stockAlerts,
        period: dateRange
      };
    } catch (error) {
      console.error('[AnalyticsService] Error in getInventoryMetrics:', error);
      return {
        totalInventoryValue: 0,
        totalMaterials: 0,
        lowStockCount: 0,
        outOfStockCount: 0,
        overstockCount: 0,
        averageStockLevel: 0,
        stockAlerts: [],
        period: dateRange
      };
    }
  }

  /**
   * Get material usage trends
   */
  async getMaterialUsage(
    branchId: string,
    dateRange: AnalyticsDateRange
  ): Promise<MaterialUsageMetric[]> {
    try {
      const [materials, transactions, recipes, orders] = await Promise.all([
        this.getMaterials(branchId),
        this.getTransactions(branchId, dateRange),
        this.getRecipes(branchId),
        this.getOrders(branchId, dateRange)
      ]);

      return materials.map((material) => {
        // Get material transactions
        const materialTransactions = transactions.filter(
          (t) => t.material_id === material.id
        );

        // Calculate usage from deductions (database uses 'adjustment_subtract' not 'deduction')
        const usedQuantity = materialTransactions
          .filter((t) => t.transaction_type === 'adjustment_subtract' || t.transaction_type === 'waste')
          .reduce((sum, t) => sum + Math.abs(t.quantity), 0);

        // Calculate purchases (database uses 'restock' not 'purchase')
        const purchasedQuantity = materialTransactions
          .filter((t) => t.transaction_type === 'restock')
          .reduce((sum, t) => sum + t.quantity, 0);

        // Calculate cost using material's cost_per_unit (transactions don't have cost_per_unit)
        const totalCost = usedQuantity * material.cost_per_unit;

        // Calculate usage rate
        const daysInPeriod = Math.max(
          1,
          Math.ceil(
            (new Date(dateRange.endDate).getTime() - new Date(dateRange.startDate).getTime()) /
              (1000 * 60 * 60 * 24)
          )
        );
        const avgDailyUsage = usedQuantity / daysInPeriod;

        // Calculate days until restock needed
        const daysUntilRestock = avgDailyUsage > 0
          ? Math.floor(material.current_stock / avgDailyUsage)
          : 999;

        return {
          materialId: material.id,
          materialName: material.name,
          usedQuantity,
          purchasedQuantity,
          totalCost,
          avgDailyUsage,
          daysUntilRestock,
          currentStock: material.current_stock,
          unit: material.unit_type
        };
      });
    } catch (error) {
      console.error('[AnalyticsService] Error in getMaterialUsage:', error);
      return [];
    }
  }

  /**
   * Get recipe cost analysis with selling prices
   */
  async getRecipeCostAnalysis(
    branchId: string
  ): Promise<RecipeCostAnalysis[]> {
    InputValidator.validateBranchId(branchId);

    try {
      const [recipes, materials, menuItems] = await Promise.all([
        this.getRecipesWithIngredients(branchId),
        this.getMaterials(branchId),
        this.getMenuItemsByBranch(branchId)
      ]);

      return recipes.map((recipe) => {
        // Type assertion - getRecipesWithIngredients returns recipes with ingredients
        const recipeWithIngredients = recipe as Recipe & { ingredients: any[] };
        
        // Calculate total recipe cost
        const totalCostResult = this.costCalculator.calculateRecipeCost(recipeWithIngredients, materials);
        const totalCost = totalCostResult.total_cost;

        // Calculate ingredient costs
        const ingredientCosts = (recipeWithIngredients.ingredients || []).map((ingredient: any) => {
          const material = materials.find((m) => m.id === ingredient.material_id);
          const cost = material ? material.cost_per_unit * ingredient.quantity : 0;

          return {
            materialId: ingredient.material_id,
            materialName: material?.name || 'Unknown',
            quantity: ingredient.quantity,
            unit: material?.unit_type || '',
            costPerUnit: material?.cost_per_unit || 0,
            totalCost: cost
          };
        });

        // Find linked menu item to get selling price
        const menuItem = menuItems.find(item => item.id === recipe.menu_item_id);
        const sellingPrice = menuItem?.base_price;
        
        // Calculate profit margin if selling price exists
        let profitMargin = 0;
        if (sellingPrice && sellingPrice > 0) {
          const profit = sellingPrice - totalCost;
          profitMargin = (profit / sellingPrice) * 100;
        }

        return {
          recipeId: recipe.id,
          recipeName: recipe.name,
          totalCost,
          ingredientCosts,
          sellingPrice,
          profitMargin,
          yield: recipe.portion_size || 1,
          costPerServing: totalCost / (recipe.portion_size || 1)
        };
      });
    } catch (error) {
      console.error('[AnalyticsService] Error in getRecipeCostAnalysis:', error);
      return [];
    }
  }

  /**
   * Create inventory snapshot for period-end reporting
   */
  async createInventorySnapshot(
    branchId: string,
    snapshotDate: Date,
    snapshotType: 'opening' | 'closing' | 'period_end'
  ): Promise<InventorySnapshot> {
    const materials = await this.getMaterials(branchId);

    // Calculate total value
    const totalValue = materials.reduce(
      (sum, m) => sum + (m.current_stock * m.cost_per_unit),
      0
    );

    // Create snapshot record
    const { data, error } = await supabase
      .from('inventory_snapshots')
      .insert({
        branch_id: branchId,
        snapshot_date: snapshotDate.toISOString(),
        snapshot_type: snapshotType,
        total_value: totalValue,
        material_count: materials.length,
        snapshot_data: {
          materials: materials.map((m) => ({
            id: m.id,
            name: m.name,
            quantity: m.current_stock,
            unit: m.unit_type,
            cost_per_unit: m.cost_per_unit,
            total_value: m.current_stock * m.cost_per_unit
          }))
        }
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  /**
   * Get cost trends over time
   */
  async getCostTrends(
    branchId: string,
    materialId: string,
    months: number = 6
  ): Promise<MaterialCostHistory[]> {
    const startDate = new Date();
    startDate.setMonth(startDate.getMonth() - months);

    const { data, error } = await supabase
      .from('material_cost_history')
      .select('*')
      .eq('material_id', materialId)
      .gte('effective_from', startDate.toISOString())
      .order('effective_from', { ascending: true });

    if (error) throw error;
    return data || [];
  }

  /**
   * Generate dashboard overview
   */
  async getDashboardOverview(
    branchId: string,
    dateRange: AnalyticsDateRange
  ) {
    // Fetch all metrics in parallel
    const [financial, waste, inventory, usage] = await Promise.all([
      this.getFinancialMetrics(branchId, dateRange),
      this.getWasteMetrics(branchId, dateRange),
      this.getInventoryMetrics(branchId, dateRange),
      this.getMaterialUsage(branchId, dateRange)
    ]);

    // Calculate top metrics
    const topUsedMaterials = usage
      .sort((a, b) => b.usedQuantity - a.usedQuantity)
      .slice(0, 5);

    const topCostlyMaterials = usage
      .sort((a, b) => b.totalCost - a.totalCost)
      .slice(0, 5);

    return {
      financial,
      waste,
      inventory,
      usage: {
        topUsedMaterials,
        topCostlyMaterials,
        totalMaterialsUsed: usage.length
      },
      alerts: inventory.stockAlerts,
      dateRange
    };
  }

  // ===== PRIVATE HELPER METHODS =====

  private async getOrders(
    branchId: string,
    dateRange: AnalyticsDateRange
  ): Promise<Order[]> {
    const { data, error } = await supabase
      .from('orders')
      .select(`
        id,
        branch_id,
        total_amount,
        status,
        created_at,
        order_items (
          id,
          order_id,
          menu_item_id,
          quantity,
          unit_price,
          total_price
        )
      `)
      .eq('branch_id', branchId)
      .gte('created_at', dateRange.startDate)
      .lte('created_at', dateRange.endDate)
      .in('status', ['completed', 'delivered']);

    if (error) {
      console.error('Error fetching orders:', error);
      return [];
    }
    
    // Map the data to match Order type
    const orders = (data || []).map((order: any) => ({
      ...order,
      order_items: (order.order_items || []).map((item: any) => ({
        id: item.id,
        order_id: item.order_id,
        menu_item_id: item.menu_item_id,
        quantity: item.quantity,
        price: item.unit_price,
        subtotal: item.total_price
      }))
    }));
    
    return orders;
  }

  private async getExpenses(
    branchId: string,
    dateRange: AnalyticsDateRange
  ): Promise<Expense[]> {
    // Expenses are stored with user_id and allocated to branches via branch_expenses table
    // For now, get user's expenses. TODO: Join with branch_expenses for branch-specific amounts
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from('expenses')
      .select(`
        id,
        user_id,
        category_id,
        title,
        amount,
        currency,
        expense_date,
        payment_method,
        is_recurring,
        status,
        created_at
      `)
      .eq('user_id', user.id)
      .gte('expense_date', dateRange.startDate)
      .lte('expense_date', dateRange.endDate);

    if (error) {
      console.error('Error fetching expenses:', error);
      // Return empty array instead of throwing to prevent breaking the whole analytics
      return [];
    }
    return data || [];
  }

  private async getTransactions(
    branchId: string,
    dateRange: AnalyticsDateRange
  ): Promise<InventoryTransaction[]> {
    const { data, error } = await supabase
      .from('inventory_transactions')
      .select(`
        id,
        material_id,
        transaction_type,
        quantity,
        unit,
        reference_id,
        created_at,
        user_id
      `)
      .eq('user_id', (await supabase.auth.getUser()).data.user?.id)
      .gte('created_at', dateRange.startDate)
      .lte('created_at', dateRange.endDate)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  private async getMaterials(branchId: string): Promise<Material[]> {
    const { data, error } = await supabase
      .from('raw_materials')
      .select(`
        id,
        name,
        branch_id,
        user_id,
        unit_type,
        current_stock,
        min_threshold,
        reorder_point,
        max_stock,
        cost_per_unit,
        supplier,
        storage_location,
        barcode,
        is_active,
        created_at,
        updated_at
      `)
      .eq('branch_id', branchId);

    if (error) throw error;
    return data || [];
  }

  private async getPhysicalCounts(
    branchId: string,
    dateRange: AnalyticsDateRange
  ): Promise<PhysicalInventoryCount[]> {
    const { data, error } = await supabase
      .from('physical_inventory_counts')
      .select(`
        id,
        material_id,
        branch_id,
        actual_stock,
        expected_stock,
        waste_amount,
        waste_percentage,
        notes,
        counted_at,
        user_id
      `)
      .eq('branch_id', branchId)
      .gte('counted_at', dateRange.startDate)
      .lte('counted_at', dateRange.endDate);

    if (error) {
      console.error('Error fetching physical counts:', error);
      return [];
    }
    return data || [];
  }

  private async getRecipes(branchId: string): Promise<Recipe[]> {
    const { data, error } = await supabase
      .from('recipes')
      .select(`
        id,
        name,
        menu_item_id,
        portion_size,
        created_at,
        updated_at,
        user_id
      `)
      .eq('user_id', (await supabase.auth.getUser()).data.user?.id);

    if (error) throw error;
    return data || [];
  }

  private async getRecipesWithIngredients(branchId: string): Promise<Recipe[]> {
    const { data, error } = await supabase
      .from('recipes')
      .select(`
        id,
        name,
        menu_item_id,
        portion_size,
        created_at,
        updated_at,
        user_id,
        ingredients:recipe_ingredients(
          id,
          recipe_id,
          material_id,
          quantity,
          unit,
          created_at
        )
      `)
      .eq('user_id', (await supabase.auth.getUser()).data.user?.id);

    if (error) throw error;
    return data || [];
  }

  private async getMenuItemsByBranch(branchId: string): Promise<Array<{id: string; base_price: number}>> {
    const { data, error } = await supabase
      .from('menu_items')
      .select('id, base_price')
      .eq('branch_id', branchId);

    if (error) {
      console.error('Error fetching menu items:', error);
      return [];
    }
    return data || [];
  }

  private calculateAverageInventoryValue(
    materials: Material[],
    transactions: InventoryTransaction[]
  ): number {
    // Calculate current value
    const currentValue = materials.reduce(
      (sum, m) => sum + (m.current_stock * m.cost_per_unit),
      0
    );

    // For simplicity, use current value as average
    // In production, you might want to query inventory_snapshots for more accurate average
    return currentValue;
  }

  private generateWasteRecommendations(wasteResult: WasteMetricsDb): string[] {
    const recommendations: string[] = [];

    if (wasteResult.total_waste_cost === 0) {
      recommendations.push('No waste detected in this period');
      return recommendations;
    }

    if (wasteResult.waste_percentage > 10) {
      recommendations.push('High waste percentage detected - consider reviewing portion sizes');
    }

    if (wasteResult.waste_percentage > 5) {
      recommendations.push('Implement physical inventory counts to track waste accurately');
    }

    const topWasteItems = (wasteResult.waste_by_material || []).slice(0, 3);
    if (topWasteItems.length > 0) {
      recommendations.push(`Focus on reducing waste for: ${topWasteItems.map(item => item.material_name).join(', ')}`);
    }

    if (recommendations.length === 0) {
      recommendations.push('Waste levels are within acceptable range');
    }

    return recommendations;
  }

  private generateStockAlerts(materials: Material[]): StockAlert[] {
    const alerts: StockAlert[] = [];

    materials.forEach((material) => {
      // Out of stock
      if (material.current_stock <= 0) {
        alerts.push({
          materialId: material.id,
          materialName: material.name,
          alertType: 'out_of_stock',
          currentStock: material.current_stock,
          unit: material.unit_type,
          severity: 'critical',
          message: `${material.name} is out of stock`
        });
      }
      // Low stock
      else if (material.reorder_point && material.current_stock <= material.reorder_point) {
        alerts.push({
          materialId: material.id,
          materialName: material.name,
          alertType: 'low_stock',
          currentStock: material.current_stock,
          reorderPoint: material.reorder_point,
          unit: material.unit_type,
          severity: 'warning',
          message: `${material.name} is below reorder point (${material.reorder_point} ${material.unit_type})`
        });
      }
      // Overstock
      else if (material.max_stock && material.current_stock > material.max_stock) {
        alerts.push({
          materialId: material.id,
          materialName: material.name,
          alertType: 'overstock',
          currentStock: material.current_stock,
          maxStock: material.max_stock,
          unit: material.unit_type,
          severity: 'info',
          message: `${material.name} exceeds maximum stock level (${material.max_stock} ${material.unit_type})`
        });
      }
    });

    return alerts.sort((a, b) => {
      const severityOrder = { critical: 0, warning: 1, info: 2 };
      return severityOrder[a.severity] - severityOrder[b.severity];
    });
  }
}

// Export singleton instance
export const analyticsService = new AnalyticsService();

